import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Column } from 'components/Layout';
import { conformToMask } from 'react-text-mask';
import { get } from 'lodash';
import cn from 'classnames';

import Header from 'components/Header';
import Footer from 'components/Footer';
import { Button } from 'components/Button';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Validator from 'components/Validator/Validator';
import { states } from 'utils/states';
import {
  nextAction,
  checkPreviousAction,
} from 'actions/workflow';
import User from 'assets/images/pending.svg';

import schema from './schema';
import style from './style.scss';

const phoneNumberMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

const IdCheckPersonalInfo = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const workflow = useSelector((state) => state.workflow);
  const key = get(searchParams, 'key');

  const formRef = useRef(null);

  useEffect(() => {
    if (!key) {
      navigate('/fsl/application');
    } else {
      dispatch(checkPreviousAction({
        data: {},
        url: `/workflows/fsl/${key}/checkin`,
        success: (response) => {
          const { validator } = props;
          const initialFormData = {
            applicantFullName: get(response, 'data.applicantFullName'),
            streetAddress1: get(response, 'data.streetAddress1'),
            city: get(response, 'data.city'),
            state: get(response, 'data.state'),
            zipCode: get(response, 'data.zipCode'),
            phoneNumber: conformToMask(get(response, 'data.phoneNumber'), phoneNumberMask).conformedValue,
          };
          validator.setValues(initialFormData);
        },
        fail: () => {
          navigate(`/fsl/autopayelection?key=${key}`, { replace: true });
        },
      }));
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleMerchantReturnClick = (e) => {
    e.preventDefault();
    if (key) {
      navigate('/dashboard');
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    if (key) {
      const { validator: { values, validate } } = props;
      if (validate(schema).isValid) {
        setIsLoading(true);
        setIsValid(true);
        dispatch(nextAction({
          data: {
            StreetAddress1: values.streetAddress1,
            City: values.city,
            State: values.state,
            ZipCode: values.zipCode,
            Ssn4: values.Ssn4,
            PhoneNumber: values.phoneNumber,
          },
          url: `/workflows/fsl/${key}/next`,
          success: (response) => {
            setIsLoading(false);
            const routeUrl = response.data && response.data.url;
            navigate(routeUrl);
          },
          fail: (error) => {
            setIsLoading(false);
            navigate('/fsl/general-error-page', {
              state: {
                data: error.data,
              },
            });
          },
        }));
      } else {
        validate(schema);
        setIsValid(false);
      }
    }
  };

  const { validator: { values, errors } } = props;
  const isSsn4Mismatch = get(workflow, 'state.isSsn4Mismatch');
  const errorMessage = get(workflow, 'state.errorMessage');

  return (
    <Fragment>
      <Header />
      <form onSubmit={handleSubmitForm.bind(null, values)} ref={formRef}>
        <div className="grid-container">
          <Grid className="grid-margin-x max-limited">
            <Column className={style['mt-30']}>
              <h2 className={style['kba-heading']}>Identity Verification</h2>
              <div className={cn(style['d-flex'], style['align-items-center'])}>
                <div>
                  <img src={User} alt="User Icon" />
                </div>
                <div className={style['pl-30']}>
                  <div>LendingUSA</div>
                </div>
              </div>
            </Column>
            <Column className={style['pt-20']}>
              {!isLoading &&
                  isSsn4Mismatch && (
                  <div className="pt-10">
                    <p className="form-note text-left">{errorMessage}</p>
                  </div>
                )}
              <h4>ID Check - Personal Information</h4>
              <p>Please confirm your home address and phone number. This information, along with your name, will be used to generate a list of questions to verify your identity.</p>
            </Column>
            <Column>
              <h4>Required Information (Home Address)</h4>
              <div className={cn(style['d-flex'], style['pt-10'])}>
                <div className={style['half-width']}>
                  <div className={cn('grid-margin-x', style['d-flex'])}>
                    <div className="small-12">
                      <Input
                        type="text"
                        name="name"
                        onChange={() => { }}
                        label="Applicant Name"
                        value={values.applicantFullName}
                        isBadgeVisible={false}
                        isDisabled
                      />
                    </div>
                  </div>
                  <div className={cn('grid-margin-x', style['d-flex'])}>
                    <div className="small-12">
                      <Input
                        type="text"
                        name="streetAddress1"
                        label="Street1"
                        value={values.streetAddress1}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!errors.streetAddress1}
                        errorMessage={errors.streetAddress1}
                      />
                    </div>
                  </div>
                  <div className={cn('grid-margin-x', style['d-flex'])}>
                    <div className="small-12">
                      <Input
                        type="text"
                        name="city"
                        value={values.city}
                        label="City"
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!errors.city}
                        errorMessage={errors.city}
                      />
                    </div>
                  </div>
                  <div className={cn('grid-margin-x', style['d-flex'])}>
                    <div className="small-12">
                      <Select
                        name="state"
                        value={values.state}
                        onChange={handleInputChange}
                        data={states}
                        label="State"
                        isRequired
                        hasError={!!errors.state}
                        errorMessage={errors.state}
                      />
                    </div>
                  </div>
                  <div className={cn('grid-margin-x', style['d-flex'], style['pt-10'])}>
                    <div className="small-12">
                      <Input
                        label="Zip"
                        isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="_____"
                        onChange={handleInputChange}
                        name="zipCode"
                        value={values.zipCode}
                        isRequired
                        hasError={!!errors.zipCode}
                        errorMessage={errors.zipCode}
                      />
                    </div>
                  </div>
                </div>
                <div className={style['half-width']}>
                  <div className={cn('grid-margin-x', style['d-flex'])}>
                    <div className="small-12">
                      <Input
                        type="tel"
                        name="Ssn4"
                        value={values.Ssn4}
                        onChange={handleInputChange}
                        label="Last 4 digits of SSN"
                        isMasked={[/\d/, /\d/, /\d/, /\d/]}
                        isRequired
                        hasError={!!errors.Ssn4}
                        errorMessage={errors.Ssn4}
                      />
                    </div>
                  </div>
                  <div className={cn('grid-margin-x', style['d-flex'])}>
                    <div className="small-12">
                      <Input
                        type="text"
                        name="phoneNumber"
                        isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        value={conformToMask(values.phoneNumber, phoneNumberMask).conformedValue}
                        onChange={handleInputChange}
                        label="Phone #"
                        isRequired
                        hasError={!!errors.phoneNumber}
                        errorMessage={errors.phoneNumber}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!isValid && (
              <div>
                <p className={style['form-note']}>You must enter required and valid information before you can continue.</p>
              </div>
              )}
              <Grid className={style['pt-10']}>
                <Column className="d-flex justify-content-center pt-30">
                  <Button
                    color="primary"
                    className="button large green"
                    onClick={handleSubmitForm}
                    isLoading={isLoading}
                    disabled={isLoading}
                  >
                      Submit
                  </Button>
                  <Button
                    className={cn('button large', style['ml-30'])}
                    onClick={handleMerchantReturnClick}
                    color="primary"
                  >
                      Cancel
                  </Button>
                </Column>
              </Grid>
              <br />
            </Column>
          </Grid>
        </div>
      </form>
      <Footer />
    </Fragment>
  );
};

IdCheckPersonalInfo.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default Validator(schema)(IdCheckPersonalInfo);
